'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as z from 'zod'

import { Button } from '../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form'
import { Input } from '../components/ui/input'
import { useUser } from '../contexts/UserContext'
import { StakeholderType } from '../types/user'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export default function Auth() {
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const navigate = useNavigate()
  const { login, user } = useUser()

  // Check for success message in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const message = urlParams.get('message')
    if (message) {
      setSuccessMessage(message)
      // Clear the URL params
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    setError('')
    setLoading(true)

    try {
      const res = await login({ email: data.email, password: data.password })

      if (res.error) {
        if (res.error?.response?.data?.errors?.length) {
          setError(res.error.response.data.errors[0]?.message)
        } else if (
          (res.error?.response?.data as { message?: string })?.message
        ) {
          setError(
            (res.error.response.data as unknown as { message: string }).message,
          )
        } else {
          setError('Something went wrong, please try again')
        }
      } else {
        // Direct redirect based on user_type and stakeholder_types from login response
        const user = 'data' in res ? res.data?.user : null
        const isDriver = user?.user_type === 'DRIVER'
        const isSubhauler = user?.stakeholder_types?.includes('SUB_HAULER')

        if (isDriver) {
          navigate('/tags')
        } else if (isSubhauler) {
          navigate('/work-orders')
        } else {
          navigate('/projects')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[40%_60%] relative">
      <div
        className="hidden md:block bg-cover bg-center"
        style={{ backgroundImage: "url('/signup.webp')" }}
      />

      {/* Logo in top left corner */}
      <img
        src="/dd_logo_white.png"
        alt="DayDaCo"
        className="hidden md:block absolute md:top-0 md:left-0 md:right-[60%] z-10 md:w-2/5 h-auto object-contain"
      />

      {/* Register link: centered on mobile, top-right on desktop */}
      <p className="absolute top-4 left-0 right-0 text-center text-sm z-10 md:left-auto md:right-4 md:text-right">
        Don't have an account yet?{' '}
        <a
          href="/register"
          className="font-semibold text-blue-600 hover:underline"
        >
          Create account here
        </a>
      </p>

      <div className="flex flex-col justify-center items-center p-8">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold mb-8 text-center">Login</h1>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john_smith@example.com"
                        className="bg-gray-50 border-gray-300"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-gray-700">
                          Password
                        </FormLabel>
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <i
                            className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-xs`}
                          />
                        </button>
                      </div>
                      <a
                        href="/forgot-password"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Forgot your password?
                      </a>
                    </div>
                    <FormControl>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••••••••"
                        className="bg-gray-50 border-gray-300"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {successMessage && (
                <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md border border-green-200">
                  {successMessage}
                </div>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg disabled:bg-[#E5E7EB] disabled:text-[#111928]"
                disabled={
                  loading || !form.watch('email') || !form.watch('password')
                }
              >
                {loading ? (
                  <i className="fa-duotone fa-solid fa-spinner-third fa-spin" />
                ) : (
                  'Login'
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
